/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import CloseIcon from "@mui/icons-material/Close";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { LoadingSpinner } from "../loading_spinner";
import { StyledAddButton, StyledCancelButton } from "../../pages/restaurant_groups/styles";
import { createSavedReportQuery, updateSavedReportQuery } from "../../api/savedReportQueryServices";
import { invalidateQueryKeys } from "../../hooks/mutations/utils/invalidateQueries";
import { getMutationErrorMessage } from "../../hooks/mutations/utils/mutationErrorMessage";
import { savedReportQueryMutationKeyGroups } from "../../hooks/mutations/queryKeyGroups/savedReportQueryMutationKeys";
import { useNotificationContext } from "../../contexts/notificationContext";
import { normalizeQueryName, toLoadedQuery } from "./useSavedQueryState";
import {
  cancelActionStyles,
  dialogActionsStyles,
  dialogHeaderStyles,
  dialogIconTileStyles,
  dialogSubtitleStyles,
  dialogTitleStyles,
  fieldInputStyles,
  fieldLabelStyles,
  helperCountStyles,
  helperRowStyles,
  optionalMarkStyles,
  primaryActionStyles,
  requiredMarkStyles,
} from "./styles";

export const NAME_MAX_LENGTH = 100;
export const DESCRIPTION_MAX_LENGTH = 500;

const REPORT_LABELS = {
  perq_report: "Perq Report",
  billing_report: "Billing Report",
};

// Helper text on the left, character count on the right.
const HelperRow = ({ text, count, max }) => (
  <Box component="span" sx={helperRowStyles}>
    <span>{text}</span>
    <Box component="span" sx={helperCountStyles}>
      {count}/{max}
    </Box>
  </Box>
);

const firstMessage = (value) => (Array.isArray(value) ? value[0] : value);
const capitalize = (text) => `${text}`.charAt(0).toUpperCase() + `${text}`.slice(1);

// 422 bodies are { errors: { attr: [...] } }; name errors go under the field, the rest above the form.
const toFormErrors = (error, name) => {
  const errors = error?.response?.data?.errors;
  if (!errors || typeof errors !== "object" || Array.isArray(errors)) {
    return { form: getMutationErrorMessage(error) };
  }

  const nameError = firstMessage(errors.name);
  const otherKey = Object.keys(errors).find((key) => key !== "name");
  const otherMessage = otherKey ? firstMessage(errors[otherKey]) : null;

  return {
    name: nameError
      ? nameError === "has already been taken"
        ? `You already have a query named '${name}'. Choose another name.`
        : `Name ${nameError}`
      : null,
    form: otherMessage
      ? otherKey === "base"
        ? capitalize(otherMessage)
        : `${capitalize(otherKey.replace(/_/g, " "))} ${otherMessage}`
      : null,
  };
};

export const SaveQueryDialog = ({
  open,
  onClose,
  reportType,
  savedFilters,
  filtersVersion,
  filtersChanged = true,
  loadedQuery,
  onSaved,
}) => {
  const queryClient = useQueryClient();
  const showNotification = useNotificationContext();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    setName(loadedQuery?.name || "");
    setDescription(loadedQuery?.description || "");
    setErrors({});
  }, [open, loadedQuery]);

  const handleSuccess = async (record, verb) => {
    await invalidateQueryKeys(queryClient, savedReportQueryMutationKeyGroups.create);
    showNotification(`Query '${record.name}' ${verb}.`, "success");
    onSaved(toLoadedQuery(record));
  };

  const createMutation = useMutation({
    mutationFn: createSavedReportQuery,
    onSuccess: (record) => handleSuccess(record, "saved"),
    onError: (error) => setErrors(toFormErrors(error, normalizeQueryName(name))),
  });

  const updateMutation = useMutation({
    mutationFn: updateSavedReportQuery,
    onSuccess: (record) => handleSuccess(record, "updated"),
    onError: (error) => setErrors(toFormErrors(error, normalizeQueryName(name))),
  });

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const trimmedName = normalizeQueryName(name);
  const isNameMissing = trimmedName === "";
  // Names are unique across all the admin's saved queries; the loaded query's is the only one the page knows.
  const isSameNameAsLoaded =
    Boolean(loadedQuery) && trimmedName.toLowerCase() === normalizeQueryName(loadedQuery.name).toLowerCase();

  const payload = () => ({
    name: trimmedName,
    description: description.trim(),
    filters: savedFilters,
    filters_version: filtersVersion,
  });

  const handleSaveNew = () => {
    setErrors({});
    createMutation.mutate({ ...payload(), report_type: reportType });
  };

  // Unchanged filters are not sent, so a rename keeps the stored ones, with any values the page left out on Apply.
  const handleUpdate = () => {
    setErrors({});
    const changes = filtersChanged ? payload() : { name: trimmedName, description: description.trim() };
    updateMutation.mutate({ ...changes, id: loadedQuery.id });
  };

  const handleClose = () => {
    if (!isSaving) onClose();
  };

  const reportLabel = REPORT_LABELS[reportType] || "report";
  const subtitle = loadedQuery
    ? `Update '${loadedQuery.name}' or save the current ${reportLabel} filters as a new query.`
    : `Save the current ${reportLabel} filters so you can reapply them in one click.`;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      data-testid="save-query-dialog"
      aria-labelledby="save-query-dialog-title"
    >
      <Box sx={dialogHeaderStyles}>
        <Box sx={dialogIconTileStyles}>
          <BookmarkBorderIcon fontSize="small" />
        </Box>
        <DialogTitle id="save-query-dialog-title" sx={{ ...dialogTitleStyles, p: 0, flex: 1, minWidth: 0 }}>
          {loadedQuery ? "Update query" : "Save query"}
        </DialogTitle>
        <IconButton
          aria-label="Close"
          size="small"
          onClick={handleClose}
          disabled={isSaving}
          sx={{ color: "#52525b" }}
          data-testid="save-query-close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Typography sx={dialogSubtitleStyles}>{subtitle}</Typography>

      <DialogContent sx={{ pt: 0 }}>
        {errors.form ? (
          <Alert severity="error" sx={{ mb: 2 }} data-testid="save-query-error">
            {errors.form}
          </Alert>
        ) : null}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          <Box>
            <Typography component="label" htmlFor="save-query-name" sx={fieldLabelStyles}>
              Name
              <Box component="span" sx={requiredMarkStyles} aria-hidden="true">
                *
              </Box>
            </Typography>
            <TextField
              id="save-query-name"
              required
              fullWidth
              size="small"
              value={name}
              onChange={(event) => {
                setName(event.target.value);
                setErrors((prev) => ({ ...prev, name: null }));
              }}
              error={Boolean(errors.name)}
              helperText={
                <HelperRow
                  text={errors.name || "Must be unique among all your saved queries"}
                  count={name.length}
                  max={NAME_MAX_LENGTH}
                />
              }
              inputProps={{ maxLength: NAME_MAX_LENGTH, "data-testid": "save-query-name" }}
              sx={fieldInputStyles}
              autoFocus
            />
          </Box>
          <Box>
            <Typography component="label" htmlFor="save-query-description" sx={fieldLabelStyles}>
              Description{" "}
              <Box component="span" sx={optionalMarkStyles}>
                (optional)
              </Box>
            </Typography>
            <TextField
              id="save-query-description"
              fullWidth
              size="small"
              multiline
              minRows={3}
              placeholder="What is this view for? e.g. Weekly check-in audit for managers"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              helperText={<HelperRow count={description.length} max={DESCRIPTION_MAX_LENGTH} />}
              inputProps={{ maxLength: DESCRIPTION_MAX_LENGTH, "data-testid": "save-query-description" }}
              sx={fieldInputStyles}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={dialogActionsStyles}>
        <StyledCancelButton
          onClick={handleClose}
          variant="outlined"
          disabled={isSaving}
          sx={cancelActionStyles}
          data-testid="save-query-cancel"
        >
          Cancel
        </StyledCancelButton>
        {loadedQuery ? (
          <React.Fragment>
            <StyledCancelButton
              onClick={handleSaveNew}
              variant="outlined"
              disabled={isSaving || isNameMissing || isSameNameAsLoaded}
              title={isSameNameAsLoaded ? "Choose a different name to save as a new query" : undefined}
              sx={cancelActionStyles}
              data-testid="save-query-save-as-new"
            >
              {createMutation.isPending ? <LoadingSpinner isLoading={true} /> : "Save as New"}
            </StyledCancelButton>
            <StyledAddButton
              onClick={handleUpdate}
              variant="contained"
              disabled={isSaving || isNameMissing}
              sx={primaryActionStyles}
              data-testid="save-query-update"
            >
              {updateMutation.isPending ? <LoadingSpinner isLoading={true} /> : "Update"}
            </StyledAddButton>
          </React.Fragment>
        ) : (
          <StyledAddButton
            onClick={handleSaveNew}
            variant="contained"
            disabled={isSaving || isNameMissing}
            sx={primaryActionStyles}
            data-testid="save-query-save"
          >
            {createMutation.isPending ? <LoadingSpinner isLoading={true} /> : "Save"}
          </StyledAddButton>
        )}
      </DialogActions>
    </Dialog>
  );
};
